import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package, ArrowUpCircle, ArrowDownCircle, TrendingUp, TrendingDown,
  AlertTriangle, DollarSign, BarChart3, Search, Calendar, Filter,
  FileText, Loader2, RefreshCw, Eye, PackageX, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockControl = () => {
  const [activeTab, setActiveTab] = useState('movements');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Data states
  const [movementsData, setMovementsData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [topProductsData, setTopProductsData] = useState(null);
  const [inactiveData, setInactiveData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [productMovementData, setProductMovementData] = useState(null);
  
  // Filters
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Dialog
  const [showProductDialog, setShowProductDialog] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, startDate, endDate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
      const cats = [...new Set(response.data.map(p => p.category).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const loadTabData = async (tab) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'movements':
          await fetchMovements();
          break;
        case 'balance':
          await fetchBalance();
          break;
        case 'top-products':
          await fetchTopProducts();
          break;
        case 'inactive':
          await fetchInactive();
          break;
        case 'financial':
          await fetchFinancial();
          break;
        case 'product-movement':
          if (selectedProduct) {
            await fetchProductMovement(selectedProduct);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading tab data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    const response = await axios.get(`${API_URL}/api/stock/movements`, {
      params: { start_date: startDate, end_date: endDate }
    });
    setMovementsData(response.data);
  };

  const fetchBalance = async () => {
    const response = await axios.get(`${API_URL}/api/stock/balance`, {
      params: { category: selectedCategory || undefined }
    });
    setBalanceData(response.data);
  };

  const fetchTopProducts = async () => {
    const response = await axios.get(`${API_URL}/api/stock/top-products`, {
      params: { start_date: startDate, end_date: endDate, limit: 20 }
    });
    setTopProductsData(response.data);
  };

  const fetchInactive = async () => {
    const response = await axios.get(`${API_URL}/api/stock/inactive-products`, {
      params: { start_date: startDate, end_date: endDate }
    });
    setInactiveData(response.data);
  };

  const fetchFinancial = async () => {
    const response = await axios.get(`${API_URL}/api/stock/financial-view`, {
      params: { category: selectedCategory || undefined }
    });
    setFinancialData(response.data);
  };

  const fetchProductMovement = async (productId) => {
    const response = await axios.get(`${API_URL}/api/stock/product-movement/${productId}`, {
      params: { start_date: startDate, end_date: endDate }
    });
    setProductMovementData(response.data);
  };

  const handleProductSelect = async (productId) => {
    setSelectedProduct(productId);
    if (productId) {
      setLoading(true);
      try {
        await fetchProductMovement(productId);
      } catch (error) {
        toast.error('Erro ao carregar movimentação do produto');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="stock-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="text-blue-600" />
            Controle de Estoque
          </h1>
          <p className="text-slate-500 mt-1">Relatórios e análises de movimentação de estoque</p>
        </div>
        
        {/* Date Filters */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-36"
            />
            <span className="text-slate-400">até</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-36"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => loadTabData(activeTab)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 lg:grid-cols-6 gap-2 h-auto p-1">
          <TabsTrigger value="movements" className="flex items-center gap-1 text-xs sm:text-sm">
            <Activity size={14} />
            <span className="hidden sm:inline">Entradas/Saídas</span>
            <span className="sm:hidden">E/S</span>
          </TabsTrigger>
          <TabsTrigger value="balance" className="flex items-center gap-1 text-xs sm:text-sm">
            <Package size={14} />
            <span className="hidden sm:inline">Saldo</span>
            <span className="sm:hidden">Saldo</span>
          </TabsTrigger>
          <TabsTrigger value="top-products" className="flex items-center gap-1 text-xs sm:text-sm">
            <TrendingUp size={14} />
            <span className="hidden sm:inline">Mais Vendidos</span>
            <span className="sm:hidden">Top</span>
          </TabsTrigger>
          <TabsTrigger value="inactive" className="flex items-center gap-1 text-xs sm:text-sm">
            <PackageX size={14} />
            <span className="hidden sm:inline">Sem Mov.</span>
            <span className="sm:hidden">Parados</span>
          </TabsTrigger>
          <TabsTrigger value="financial" className="flex items-center gap-1 text-xs sm:text-sm">
            <DollarSign size={14} />
            <span className="hidden sm:inline">Financeiro</span>
            <span className="sm:hidden">$</span>
          </TabsTrigger>
          <TabsTrigger value="product-movement" className="flex items-center gap-1 text-xs sm:text-sm">
            <BarChart3 size={14} />
            <span className="hidden sm:inline">Por Produto</span>
            <span className="sm:hidden">Produto</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Entradas e Saídas */}
        <TabsContent value="movements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="text-blue-600" />
                Entradas e Saídas de Estoque
              </CardTitle>
              <CardDescription>
                Todas as movimentações de estoque no período selecionado
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : movementsData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <ArrowDownCircle className="text-emerald-600" size={20} />
                          <span className="text-sm text-emerald-600">Entradas</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-700 mt-1">
                          {movementsData.summary.qty_entradas} un
                        </p>
                        <p className="text-sm text-emerald-600">
                          {formatCurrency(movementsData.summary.total_entradas)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <ArrowUpCircle className="text-red-600" size={20} />
                          <span className="text-sm text-red-600">Saídas</span>
                        </div>
                        <p className="text-2xl font-bold text-red-700 mt-1">
                          {movementsData.summary.qty_saidas} un
                        </p>
                        <p className="text-sm text-red-600">
                          {formatCurrency(movementsData.summary.total_saidas)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <Package className="text-blue-600" size={20} />
                          <span className="text-sm text-blue-600">Saldo Qtd</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-700 mt-1">
                          {movementsData.summary.saldo_quantidade} un
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="text-purple-600" size={20} />
                          <span className="text-sm text-purple-600">Saldo Valor</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-700 mt-1">
                          {formatCurrency(movementsData.summary.saldo_valor)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Movements Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Documento</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Valor Unit.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {movementsData.movements.slice(0, 100).map((mov, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{formatDate(mov.date)}</TableCell>
                            <TableCell>
                              <Badge className={mov.type === 'entrada' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                                {mov.type === 'entrada' ? '↓ Entrada' : '↑ Saída'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{mov.document}</TableCell>
                            <TableCell className="font-medium">{mov.product_name}</TableCell>
                            <TableCell className="text-center">{mov.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(mov.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(mov.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {movementsData.movements.length > 100 && (
                      <p className="text-center text-slate-500 text-sm mt-4">
                        Mostrando 100 de {movementsData.movements.length} movimentações
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-center text-slate-500 py-8">Selecione um período para visualizar</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Saldo em Estoque */}
        <TabsContent value="balance" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="text-blue-600" />
                    Saldo em Estoque
                  </CardTitle>
                  <CardDescription>
                    Posição atual do estoque por produto e categoria
                  </CardDescription>
                </div>
                <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Todas categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas categorias</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : balanceData && balanceData.summary ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Itens</p>
                        <p className="text-2xl font-bold">{balanceData.summary.total_items || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Quantidade</p>
                        <p className="text-2xl font-bold">{balanceData.summary.total_quantity || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-red-600">Valor Custo</p>
                        <p className="text-xl font-bold text-red-700">{formatCurrency(balanceData.summary.total_value_cost)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-emerald-600">Valor Venda</p>
                        <p className="text-xl font-bold text-emerald-700">{formatCurrency(balanceData.summary.total_value_sale)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-blue-600">Margem</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(balanceData.summary.margin)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Products by Category */}
                  {balanceData.by_category && Object.entries(balanceData.by_category).map(([category, data]) => (
                    <Card key={category} className="mb-4">
                      <CardHeader className="py-3">
                        <CardTitle className="text-lg flex items-center justify-between">
                          <span>{category}</span>
                          <div className="flex gap-4 text-sm font-normal">
                            <span>Qtd: <strong>{data.total_quantity}</strong></span>
                            <span>Custo: <strong>{formatCurrency(data.total_value_cost)}</strong></span>
                            <span>Venda: <strong>{formatCurrency(data.total_value_sale)}</strong></span>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Código</TableHead>
                              <TableHead>Produto</TableHead>
                              <TableHead className="text-center">Estoque</TableHead>
                              <TableHead className="text-center">Mín.</TableHead>
                              <TableHead className="text-right">Custo</TableHead>
                              <TableHead className="text-right">Venda</TableHead>
                              <TableHead className="text-right">Valor Estoque</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.products.map((product) => (
                              <TableRow key={product.id}>
                                <TableCell className="font-mono text-sm">{product.code}</TableCell>
                                <TableCell className="font-medium">{product.name}</TableCell>
                                <TableCell className="text-center">{product.stock} {product.unit}</TableCell>
                                <TableCell className="text-center">{product.min_stock}</TableCell>
                                <TableCell className="text-right">{formatCurrency(product.cost_price)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(product.sale_price)}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(product.value_sale)}</TableCell>
                                <TableCell>
                                  <Badge className={
                                    product.status === 'zerado' ? 'bg-red-100 text-red-800' :
                                    product.status === 'baixo' ? 'bg-amber-100 text-amber-800' :
                                    'bg-emerald-100 text-emerald-800'
                                  }>
                                    {product.status === 'zerado' ? 'Zerado' :
                                     product.status === 'baixo' ? 'Baixo' : 'OK'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                <p className="text-center text-slate-500 py-8">Carregando...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Produtos com Maior Circulação */}
        <TabsContent value="top-products" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="text-emerald-600" />
                Produtos com Maior Circulação
              </CardTitle>
              <CardDescription>
                Ranking dos produtos mais vendidos no período
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : topProductsData ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top by Quantity */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Top por Quantidade Vendida</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Produto</TableHead>
                            <TableHead className="text-center">Qtd</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(topProductsData.top_by_quantity || []).map((product, idx) => (
                            <TableRow key={product.product_id || idx}>
                              <TableCell>
                                <Badge variant={idx < 3 ? 'default' : 'secondary'} className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : ''}>
                                  {idx + 1}º
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{product.product_name}</TableCell>
                              <TableCell className="text-center font-bold">{product.total_quantity}</TableCell>
                              <TableCell className="text-right">{formatCurrency(product.total_value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {(!topProductsData.top_by_quantity || topProductsData.top_by_quantity.length === 0) && (
                        <p className="text-center text-slate-500 py-4">Nenhum produto vendido no período</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top by Value */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Top por Valor Vendido</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Produto</TableHead>
                            <TableHead className="text-center">Qtd</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(topProductsData.top_by_value || []).map((product, idx) => (
                            <TableRow key={product.product_id || idx}>
                              <TableCell>
                                <Badge variant={idx < 3 ? 'default' : 'secondary'} className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : ''}>
                                  {idx + 1}º
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{product.product_name}</TableCell>
                              <TableCell className="text-center">{product.total_quantity}</TableCell>
                              <TableCell className="text-right font-bold">{formatCurrency(product.total_value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {(!topProductsData.top_by_value || topProductsData.top_by_value.length === 0) && (
                        <p className="text-center text-slate-500 py-4">Nenhum produto vendido no período</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">Selecione um período para visualizar</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Produtos sem Movimentação */}
        <TabsContent value="inactive" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PackageX className="text-amber-600" />
                Produtos sem Movimentação
              </CardTitle>
              <CardDescription>
                Produtos que não tiveram nenhuma entrada ou saída no período
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : inactiveData && inactiveData.summary ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-amber-600">Produtos Parados</p>
                        <p className="text-3xl font-bold text-amber-700">{inactiveData.summary.total_inactive || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Total de Produtos</p>
                        <p className="text-3xl font-bold">{inactiveData.summary.total_products || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-red-600">% Parados</p>
                        <p className="text-3xl font-bold text-red-700">{inactiveData.summary.percentage_inactive || 0}%</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-purple-600">Valor Parado</p>
                        <p className="text-2xl font-bold text-purple-700">{formatCurrency(inactiveData.summary.total_value_stuck)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Inactive Products Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-center">Estoque</TableHead>
                        <TableHead className="text-right">Preço</TableHead>
                        <TableHead className="text-right">Valor em Estoque</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(inactiveData.inactive_products || []).map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-mono text-sm">{product.code}</TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.category}</TableCell>
                          <TableCell className="text-center">{product.stock}</TableCell>
                          <TableCell className="text-right">{formatCurrency(product.sale_price)}</TableCell>
                          <TableCell className="text-right font-medium text-amber-600">{formatCurrency(product.value_in_stock)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {(!inactiveData.inactive_products || inactiveData.inactive_products.length === 0) && (
                    <p className="text-center text-emerald-600 py-8">
                      Todos os produtos tiveram movimentação no período! 
                    </p>
                  )}
                </>
              ) : (
                <p className="text-center text-slate-500 py-8">Selecione um período para visualizar</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Visão Financeira */}
        <TabsContent value="financial" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="text-emerald-600" />
                    Visão Financeira do Estoque
                  </CardTitle>
                  <CardDescription>
                    Valor total dos produtos pelo preço de custo e preço de venda
                  </CardDescription>
                </div>
                <Select value={selectedCategory || "all"} onValueChange={(v) => { setSelectedCategory(v === "all" ? "" : v); }}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Todas categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas categorias</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : financialData && financialData.summary ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Produtos</p>
                        <p className="text-2xl font-bold">{financialData.summary.total_items || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-red-600">Total Custo</p>
                        <p className="text-xl font-bold text-red-700">{formatCurrency(financialData.summary.total_cost)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-blue-600">Total Venda</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(financialData.summary.total_sale)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-emerald-600">Margem Total</p>
                        <p className="text-xl font-bold text-emerald-700">{formatCurrency(financialData.summary.total_margin)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-purple-600">Margem %</p>
                        <p className="text-xl font-bold text-purple-700">{financialData.summary.average_margin_percent || 0}%</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Products Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-center">Estoque</TableHead>
                        <TableHead className="text-right">Custo Unit.</TableHead>
                        <TableHead className="text-right">Venda Unit.</TableHead>
                        <TableHead className="text-right">Valor Custo</TableHead>
                        <TableHead className="text-right">Valor Venda</TableHead>
                        <TableHead className="text-right">Margem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(financialData.products || []).map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-mono text-sm">{product.code}</TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.category}</TableCell>
                          <TableCell className="text-center">{product.stock} {product.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(product.cost_price)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(product.sale_price)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(product.value_cost)}</TableCell>
                          <TableCell className="text-right text-blue-600">{formatCurrency(product.value_sale)}</TableCell>
                          <TableCell className="text-right">
                            <span className={product.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {formatCurrency(product.margin)} ({product.margin_percent}%)
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <p className="text-center text-slate-500 py-8">Carregando...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 6: Movimentação por Produto */}
        <TabsContent value="product-movement" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="text-blue-600" />
                    Movimentação por Produto
                  </CardTitle>
                  <CardDescription>
                    Histórico detalhado de movimentação de um produto específico
                  </CardDescription>
                </div>
                <Select value={selectedProduct} onValueChange={handleProductSelect}>
                  <SelectTrigger className="w-72">
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.code || product.sku} - {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : productMovementData ? (
                <>
                  {/* Product Info */}
                  <Card className="mb-6 bg-slate-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Código</p>
                          <p className="font-mono font-medium">{productMovementData.product.code}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-slate-500">Produto</p>
                          <p className="font-medium">{productMovementData.product.name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Estoque Atual</p>
                          <p className="font-bold text-blue-600">{productMovementData.product.current_stock}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Preço Custo</p>
                          <p className="font-medium">{formatCurrency(productMovementData.product.cost_price)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Preço Venda</p>
                          <p className="font-medium">{formatCurrency(productMovementData.product.sale_price)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Saldo Inicial</p>
                        <p className="text-2xl font-bold">{productMovementData.summary.initial_balance}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-emerald-600">Entradas</p>
                        <p className="text-2xl font-bold text-emerald-700">+{productMovementData.summary.total_entradas}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-red-600">Saídas</p>
                        <p className="text-2xl font-bold text-red-700">-{productMovementData.summary.total_saidas}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-blue-600">Saldo Final</p>
                        <p className="text-2xl font-bold text-blue-700">{productMovementData.summary.final_balance}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-purple-600">Movimentações</p>
                        <p className="text-2xl font-bold text-purple-700">{productMovementData.summary.movement_count}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Movements Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Documento</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-center">Quantidade</TableHead>
                        <TableHead className="text-right">Valor Unit.</TableHead>
                        <TableHead className="text-center">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productMovementData.movements.map((mov, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{formatDate(mov.date)}</TableCell>
                          <TableCell>
                            <Badge className={mov.type === 'entrada' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                              {mov.type === 'entrada' ? '↓ Entrada' : '↑ Saída'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{mov.document}</TableCell>
                          <TableCell className="text-sm">{mov.description}</TableCell>
                          <TableCell className={`text-center font-bold ${mov.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {mov.quantity > 0 ? '+' : ''}{mov.quantity}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(mov.unit_price)}</TableCell>
                          <TableCell className="text-center font-bold">{mov.balance}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {productMovementData.movements.length === 0 && (
                    <p className="text-center text-slate-500 py-8">
                      Nenhuma movimentação encontrada no período
                    </p>
                  )}
                </>
              ) : (
                <div className="text-center text-slate-500 py-12">
                  <Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Selecione um produto para visualizar a movimentação</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StockControl;
